import { byLocale, type LocaleTable } from "@/lib/locale";
import type { Locale } from "@/lib/locale";
// AUTO-GENERATED by scripts/generate-provinces.mjs from data/vn-units.json.
// Do not edit by hand — re-run the generator if the dataset changes.
//
// Single source of truth for Vietnam's 34 post-2025-reform provinces +
// centrally-managed cities. Drives the apply form, dashboard editor, search
// filters, suggestion hrefs, validation enums, and DB columns
// (`professionals.location`, `professional_applications.city`).
//
// Pre-July-2025 Vietnam had 63 provinces with a 3-tier admin structure
// (province → district → ward). The reform consolidated to 34 provinces and
// abolished the district tier entirely — this file reflects the new shape.
// If you need to map old-province bookmarks, that lives in a separate alias
// table elsewhere (we hard-cut on launch).

export interface Province {
  /** URL-safe slug — used in `?location=ha-noi` and DB storage. */
  readonly slug: ProvinceSlug;
  /** Vietnamese display name with diacritics, e.g. "Hà Nội". */
  readonly name: string;
  /** ASCII English name, e.g. "Ha Noi". */
  readonly nameEn: string;
  /** Source dataset numeric code, kept for traceability against the JSON. */
  readonly code: string;
  /** True for Centrally-managed Cities (`Thành phố`), false for Provinces (`Tỉnh`). */
  readonly isCity: boolean;
}

export const PROVINCE_OPTIONS = [
  {
    "slug": "an-giang",
    "name": "An Giang",
    "nameEn": "An Giang",
    "code": "91",
    "isCity": false
  },
  {
    "slug": "bac-ninh",
    "name": "Bắc Ninh",
    "nameEn": "Bac Ninh",
    "code": "24",
    "isCity": false
  },
  {
    "slug": "ca-mau",
    "name": "Cà Mau",
    "nameEn": "Ca Mau",
    "code": "96",
    "isCity": false
  },
  {
    "slug": "can-tho",
    "name": "Cần Thơ",
    "nameEn": "Can Tho",
    "code": "92",
    "isCity": true
  },
  {
    "slug": "cao-bang",
    "name": "Cao Bằng",
    "nameEn": "Cao Bang",
    "code": "04",
    "isCity": false
  },
  {
    "slug": "da-nang",
    "name": "Đà Nẵng",
    "nameEn": "Da Nang",
    "code": "48",
    "isCity": true
  },
  {
    "slug": "dak-lak",
    "name": "Đắk Lắk",
    "nameEn": "Dak Lak",
    "code": "66",
    "isCity": false
  },
  {
    "slug": "dien-bien",
    "name": "Điện Biên",
    "nameEn": "Dien Bien",
    "code": "11",
    "isCity": false
  },
  {
    "slug": "dong-nai",
    "name": "Đồng Nai",
    "nameEn": "Dong Nai",
    "code": "75",
    "isCity": false
  },
  {
    "slug": "dong-thap",
    "name": "Đồng Tháp",
    "nameEn": "Dong Thap",
    "code": "82",
    "isCity": false
  },
  {
    "slug": "gia-lai",
    "name": "Gia Lai",
    "nameEn": "Gia Lai",
    "code": "52",
    "isCity": false
  },
  {
    "slug": "ha-noi",
    "name": "Hà Nội",
    "nameEn": "Ha Noi",
    "code": "01",
    "isCity": true
  },
  {
    "slug": "ha-tinh",
    "name": "Hà Tĩnh",
    "nameEn": "Ha Tinh",
    "code": "42",
    "isCity": false
  },
  {
    "slug": "hai-phong",
    "name": "Hải Phòng",
    "nameEn": "Hai Phong",
    "code": "31",
    "isCity": true
  },
  {
    "slug": "ho-chi-minh",
    "name": "TP. Hồ Chí Minh",
    "nameEn": "Ho Chi Minh City",
    "code": "79",
    "isCity": true
  },
  {
    "slug": "hue",
    "name": "Huế",
    "nameEn": "Hue",
    "code": "46",
    "isCity": true
  },
  {
    "slug": "hung-yen",
    "name": "Hưng Yên",
    "nameEn": "Hung Yen",
    "code": "33",
    "isCity": false
  },
  {
    "slug": "khanh-hoa",
    "name": "Khánh Hòa",
    "nameEn": "Khanh Hoa",
    "code": "56",
    "isCity": false
  },
  {
    "slug": "lai-chau",
    "name": "Lai Châu",
    "nameEn": "Lai Chau",
    "code": "12",
    "isCity": false
  },
  {
    "slug": "lam-dong",
    "name": "Lâm Đồng",
    "nameEn": "Lam Dong",
    "code": "68",
    "isCity": false
  },
  {
    "slug": "lang-son",
    "name": "Lạng Sơn",
    "nameEn": "Lang Son",
    "code": "20",
    "isCity": false
  },
  {
    "slug": "lao-cai",
    "name": "Lào Cai",
    "nameEn": "Lao Cai",
    "code": "15",
    "isCity": false
  },
  {
    "slug": "nghe-an",
    "name": "Nghệ An",
    "nameEn": "Nghe An",
    "code": "40",
    "isCity": false
  },
  {
    "slug": "ninh-binh",
    "name": "Ninh Bình",
    "nameEn": "Ninh Binh",
    "code": "37",
    "isCity": false
  },
  {
    "slug": "phu-tho",
    "name": "Phú Thọ",
    "nameEn": "Phu Tho",
    "code": "25",
    "isCity": false
  },
  {
    "slug": "quang-ngai",
    "name": "Quảng Ngãi",
    "nameEn": "Quang Ngai",
    "code": "51",
    "isCity": false
  },
  {
    "slug": "quang-ninh",
    "name": "Quảng Ninh",
    "nameEn": "Quang Ninh",
    "code": "22",
    "isCity": false
  },
  {
    "slug": "quang-tri",
    "name": "Quảng Trị",
    "nameEn": "Quang Tri",
    "code": "44",
    "isCity": false
  },
  {
    "slug": "son-la",
    "name": "Sơn La",
    "nameEn": "Son La",
    "code": "14",
    "isCity": false
  },
  {
    "slug": "tay-ninh",
    "name": "Tây Ninh",
    "nameEn": "Tay Ninh",
    "code": "80",
    "isCity": false
  },
  {
    "slug": "thai-nguyen",
    "name": "Thái Nguyên",
    "nameEn": "Thai Nguyen",
    "code": "19",
    "isCity": false
  },
  {
    "slug": "thanh-hoa",
    "name": "Thanh Hóa",
    "nameEn": "Thanh Hoa",
    "code": "38",
    "isCity": false
  },
  {
    "slug": "tuyen-quang",
    "name": "Tuyên Quang",
    "nameEn": "Tuyen Quang",
    "code": "08",
    "isCity": false
  },
  {
    "slug": "vinh-long",
    "name": "Vĩnh Long",
    "nameEn": "Vinh Long",
    "code": "86",
    "isCity": false
  }
] as const satisfies readonly Province[];

export const PROVINCE_SLUGS = [
  "an-giang",
  "bac-ninh",
  "ca-mau",
  "can-tho",
  "cao-bang",
  "da-nang",
  "dak-lak",
  "dien-bien",
  "dong-nai",
  "dong-thap",
  "gia-lai",
  "ha-noi",
  "ha-tinh",
  "hai-phong",
  "ho-chi-minh",
  "hue",
  "hung-yen",
  "khanh-hoa",
  "lai-chau",
  "lam-dong",
  "lang-son",
  "lao-cai",
  "nghe-an",
  "ninh-binh",
  "phu-tho",
  "quang-ngai",
  "quang-ninh",
  "quang-tri",
  "son-la",
  "tay-ninh",
  "thai-nguyen",
  "thanh-hoa",
  "tuyen-quang",
  "vinh-long",
] as const;

export type ProvinceSlug = (typeof PROVINCE_SLUGS)[number];

// Special non-province sentinel for "Remote / online-only" pros. Kept
// separate from `ProvinceSlug` so accidental usage (e.g. `formatProvince("remote")`)
// is a type error rather than a silent wrong-string lookup.
export const REMOTE_SLUG = "remote" as const;
export type RemoteSlug = typeof REMOTE_SLUG;

/** What's actually stored in `professionals.location` — a province, remote, or empty. */
export type LocationSlug = ProvinceSlug | RemoteSlug | "";

/** All non-empty values accepted by the DB `location` column. */
export const LOCATION_SLUGS = [...PROVINCE_SLUGS, REMOTE_SLUG] as const;

// Quick-pick chips shown above the province search input. Order is alphabetical
// by English name to match the rest of the directory's sort order. To re-rank
// (e.g. by pro density), edit this constant — keep it pinned at 7 items so
// the chip row doesn't wrap on mobile.
export const DEFAULT_PROVINCE_SLUGS = ["can-tho","da-nang","dong-nai","hai-phong","ha-noi","ho-chi-minh","hue"] as const satisfies readonly ProvinceSlug[];

// ── Lookup helpers ─────────────────────────────────────────────────────────

const BY_SLUG: Record<string, Province> = Object.fromEntries(
  PROVINCE_OPTIONS.map((p) => [p.slug, p]),
);

// Locale type imported by value so this module stays free of `next/headers`
// (it's bundled into client code too). The actual locale value comes from
// callers — server components via `getServerLocale()` in `locale-server.ts`,
// client components via the `useLocale()` hook.
// Was a hand-copied union; derives from `routing.locales` now.
type UiLocale = Locale;

/** Pick the Vietnamese (`name`) or English (`nameEn`) label per locale. */
/**
 * A province's display name in `locale`.
 *
 * ⛔ EXPORTED because four call sites had each re-implemented it inline
 * (`ProvinceFilter`, `OfficesForm`, `ApplicationForm`, `RequestForm`), which is
 * the same label rule written in five places: a new locale, or a change to how
 * the dataset carries names, would have had to find all of them.
 */
export function provinceLabel(p: Province, locale: string): string {
  // VN = Vietnamese; other locales use the English label, except where French
  // has its own established name for the city (FR_EXONYMS). The dataset is
  // GENERATED from data/vn-units.json and carries only VI + EN, so French
  // lives here rather than in it.
  return byLocale(locale, {
    en: p.nameEn,
    vi: p.name,
    fr: FR_EXONYMS[p.slug] ?? p.nameEn,
    "zh-Hans": ZH_HANS_NAMES[p.slug] ?? p.nameEn,
    "zh-Hant": ZH_HANT_NAMES[p.slug] ?? p.nameEn,
  });
}

/** The provinces French writes differently from English. Everything else
 *  ("Da Nang", "Can Tho") is the same in both, so it is not listed. */
const FR_EXONYMS: Readonly<Record<string, string>> = {
  "ho-chi-minh": "Hô Chi Minh\u2011Ville",
  "ha-noi": "Hanoï",
  hue: "Hué",
};

/** Chinese names for the cities that have an established one. The rest of
 *  the 34 provinces read the English label in Chinese, as the dataset carries
 *  only VI + EN. */
const ZH_HANS_NAMES: Readonly<Record<string, string>> = {
  "ho-chi-minh": "胡志明市",
  "ha-noi": "河内",
  "da-nang": "岘港",
  "hai-phong": "海防",
  "can-tho": "芹苴",
  hue: "顺化",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const ZH_HANT_NAMES: Readonly<Record<string, string>> = {
  "ho-chi-minh": "胡志明市",
  "ha-noi": "河內",
  "da-nang": "峴港",
  "hai-phong": "海防",
  "can-tho": "芹苴",
  hue: "順化",
};

// EN-required, rest optional: see `LocaleTable`.
const REMOTE_LABELS: LocaleTable<string> = {
  en: "Remote",
  vi: "Từ xa",
  fr: "À distance",
  "zh-Hans": "远程",
  "zh-Hant": "遠程",
};

/**
 * Display label for a stored location value.
 *
 * @param value  Slug (`"ha-noi"` / `"remote"` / `""` / legacy / unknown).
 * @param locale UI locale. Defaults to `"en"` — the site-wide default for
 *               unauthenticated / first-visit users. Pass `"vi"` to render
 *               Vietnamese with diacritics ("Hà Nội"). `"fr"` uses the
 *               English label except for the French exonyms (`FR_EXONYMS`).
 *
 * Examples (locale = "en"):
 *   formatLocation("ha-noi")        → "Ha Noi"
 *   formatLocation("remote")        → "Remote"
 *   formatLocation("")              → ""
 *   formatLocation("unknown-old")   → "" (silent degrade)
 *
 * Examples (locale = "vi"):
 *   formatLocation("ha-noi", "vi")  → "Hà Nội"
 *   formatLocation("remote", "vi")  → "Từ xa"
 */
export function formatLocation(
  value: string | null | undefined,
  locale: UiLocale = "en",
): string {
  if (!value) return "";
  if (value === REMOTE_SLUG) return byLocale(locale, REMOTE_LABELS);
  const p = BY_SLUG[value];
  return p ? provinceLabel(p, locale) : "";
}

/**
 * Format a professional's office provinces into one label, e.g.
 * "Ho Chi Minh City · Ha Noi · Da Nang". The caller supplies the slugs in the
 * intended order (main office first); duplicates and unknown/empty slugs are
 * dropped. Single source of truth for the multi-office location string shared
 * by the profile hero, the share card, and the OG card — so the separator and
 * ordering never drift between surfaces.
 */
export function formatOfficeLocations(
  slugs: (string | null | undefined)[],
  locale: UiLocale = "en",
): string {
  return [...new Set(slugs.filter((s): s is string => !!s))]
    .map((s) => formatLocation(s, locale))
    .filter(Boolean)
    .join(" · ");
}

/**
 * Province-only formatter — `remote` isn't a province so returns "" for
 * it. Use on physical-address surfaces (office address, Google Maps
 * deep-links) where the "Remote" sentinel makes no sense.
 */
export function formatProvince(
  slug: string | null | undefined,
  locale: UiLocale = "en",
): string {
  if (!slug) return "";
  const p = BY_SLUG[slug];
  return p ? provinceLabel(p, locale) : "";
}

/** Narrow an unknown value to a `ProvinceSlug`, or "" if unrecognised. */
export function toProvinceSlugOrEmpty(value: unknown): ProvinceSlug | "" {
  if (typeof value !== "string") return "";
  return value in BY_SLUG ? (value as ProvinceSlug) : "";
}

/** Narrow an unknown value to a `LocationSlug` (province | remote | ""). */
export function toLocationSlugOrEmpty(value: unknown): LocationSlug {
  if (typeof value !== "string" || value === "") return "";
  if (value === REMOTE_SLUG) return REMOTE_SLUG;
  return value in BY_SLUG ? (value as ProvinceSlug) : "";
}

export function isProvinceSlug(value: unknown): value is ProvinceSlug {
  return typeof value === "string" && value in BY_SLUG;
}

// ── Free-text alias map ────────────────────────────────────────────────────
//
// Lets `q=` free-text search match province slugs via common aliases readers
// type (English-only spellings, historical names, capitalisation variants).
// Used by `getProfessionalsForSearch` to expand text-query matching against
// the slug-stored `location` column.

export const PROVINCE_ALIASES: Record<string, ProvinceSlug> = {
  // Ho Chi Minh City — historical + colloquial
  "saigon": "ho-chi-minh",
  "sai gon": "ho-chi-minh",
  "sài gòn": "ho-chi-minh",
  "hcm": "ho-chi-minh",
  "hcmc": "ho-chi-minh",
  "ho chi minh city": "ho-chi-minh",
  "ho chi minh": "ho-chi-minh",
  "hồ chí minh": "ho-chi-minh",

  // Hà Nội
  "hanoi": "ha-noi",
  "hà nội": "ha-noi",

  // Đà Nẵng
  "danang": "da-nang",
  "đà nẵng": "da-nang",

  // Hải Phòng
  "haiphong": "hai-phong",
  "hải phòng": "hai-phong",

  // Huế
  "huế": "hue",

  // Cần Thơ
  "cantho": "can-tho",
  "cần thơ": "can-tho",
};

// Inverse of PROVINCE_ALIASES: province slug → its colloquial / historical
// aliases. Lets the searchable <Select> (apply form + OfficesForm) and the
// ProvinceFilter resolve "saigon" / "hcmc" → Ho Chi Minh etc. — parity with
// the hero search, which already expands the same aliases. Built once.
const PROVINCE_ALIAS_KEYWORDS: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const [alias, slug] of Object.entries(PROVINCE_ALIASES)) {
    (map[slug] ??= []).push(alias);
  }
  return map;
})();

/** Colloquial / historical search aliases for a province slug (may be empty). */
export function provinceAliasKeywords(slug: string): string[] {
  return PROVINCE_ALIAS_KEYWORDS[slug] ?? [];
}

/**
 * Diacritic-strip + lowercase. Mirrors the same normalisation used by the
 * ward index so "ha noi" and "Hà Nội" both collapse to "ha noi" — meaning
 * the alias function below works for every province in the dataset without
 * needing an explicit entry per name.
 */
function normaliseAliasKey(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
}

// Precomputed normalised lookup over every province's VN + EN name + slug.
// Built once at module load so the alias function is O(1).
const NORMALISED_PROVINCE_MAP: Record<string, ProvinceSlug> = (() => {
  const map: Record<string, ProvinceSlug> = {};
  for (const p of PROVINCE_OPTIONS) {
    map[normaliseAliasKey(p.slug)] = p.slug;
    map[normaliseAliasKey(p.name)] = p.slug;
    map[normaliseAliasKey(p.nameEn)] = p.slug;
  }
  return map;
})();

/**
 * Best-effort free-text → province slug resolution. Tries:
 *   1. Hard-coded `PROVINCE_ALIASES` (historical / colloquial — "Saigon",
 *      "HCM", "Sài Gòn", etc.). Matched against the lowercase trimmed input
 *      as-is so diacritic-correct strings ("Hồ Chí Minh") work too.
 *   2. Normalised province / city name match. Diacritic-stripping means
 *      "ha noi", "Hà Nội", "HA NOI", and the slug "ha-noi" all resolve.
 *
 * Returns `null` when nothing matches. Used by the search query builder
 * (`getProfessionalsForSearch`) to expand q-text matching against the
 * slug-stored `location` column.
 */
export function aliasToProvinceSlug(text: string): ProvinceSlug | null {
  const lc = text.trim().toLowerCase();
  if (!lc) return null;
  if (PROVINCE_ALIASES[lc]) return PROVINCE_ALIASES[lc];
  return NORMALISED_PROVINCE_MAP[normaliseAliasKey(text)] ?? null;
}

// Remote sentinel recognised by its EN/VI/FR display labels (diacritic-
// stripped) plus colloquial English forms. The VI label "Từ xa" matters most:
// it's what the locale-switched UI shows for `remote`, so a VI user types it
// expecting results. Provinces resolve by their VI names for free via
// NORMALISED_PROVINCE_MAP; `remote` is a sentinel outside PROVINCE_OPTIONS, so
// its labels must be listed here. normaliseAliasKey strips diacritics →
// "Từ xa" collapses to "tu xa".
const REMOTE_ALIAS_KEYS = new Set<string>([
  ...Object.values(REMOTE_LABELS).map(normaliseAliasKey), // remote · tu xa · a distance
  "online",
  "remote / online",
]);

/**
 * Like `aliasToProvinceSlug` but also recognises the `remote` location
 * sentinel by its EN/VI/FR labels + "online". Returns the wider `LocationSlug`
 * union.
 */
export function aliasToLocationSlug(text: string): LocationSlug | null {
  const lc = text.trim().toLowerCase();
  if (!lc) return null;
  if (REMOTE_ALIAS_KEYS.has(normaliseAliasKey(text))) {
    return REMOTE_SLUG;
  }
  return aliasToProvinceSlug(text);
}
